import { collection, getDocs, limit, startAfter, query, doc, getDoc, writeBatch, orderBy, where } from 'firebase/firestore'
import { ref } from 'vue'
import firebase from '../../firebase'
import { httpsCallable } from 'firebase/functions'

const getCollection = () => {
  const documents = ref([])
  const pageSize = ref(15)
  const lastVisible = ref(null)
  const loading = ref(false)
  const allDataLoaded = ref(false)

  const convertTargetBasedOnType = (target, type) => {
    if (type === 'number') {
      return Number(target)
    } else if (type === 'boolean') {
      return target === 'true'
    } else {
      return target
    }
  }

  const fetchDocuments = async (collectionId, orderQuery, whereQuery, whereQueryShouldApply) => {
    const collRef = collection(firebase.db, collectionId)

    const queries = []

    if (whereQuery && whereQuery.length > 0 && whereQueryShouldApply) {
      whereQuery.forEach(filter => {
        queries.push(where(filter.field, filter.condition, convertTargetBasedOnType(filter.target, filter.dataType)))
      })
    }

    // Apply ordering if provided
    if (orderQuery && orderQuery.field && orderQuery.direction) {
      queries.push(orderBy(orderQuery.field, orderQuery.direction))
    }

    queries.push(limit(pageSize.value))
    const q = query(collRef, ...queries)

    documents.value = []
    lastVisible.value = null
    allDataLoaded.value = false
    loading.value = true
    try {
      const docSnapshots = await getDocs(q)
      documents.value = docSnapshots.docs.map(doc => {
        return {
          id: doc.id,
          ...doc.data()
        }
      })
      if (docSnapshots.docs.length < pageSize.value) {
        allDataLoaded.value = true
      }
      lastVisible.value = docSnapshots.docs[docSnapshots.docs.length - 1]
    } catch (error) {
      console.error('Error fetching documents: ', error)
    } finally {
      loading.value = false
    }
  }

  const fetchNextPage = async (collectionId, sortQuery, whereQuery, whereQueryShouldApply) => {
    if (!lastVisible.value) return

    const collRef = collection(firebase.db, collectionId)
    const queries = []

    // Apply whereQuery conditions
    if (whereQuery && whereQuery.length > 0 && whereQueryShouldApply) {
      whereQuery.forEach(filter => {
        queries.push(where(filter.field, filter.condition, convertTargetBasedOnType(filter.target, filter.dataType)))
      })
    }

    // Apply ordering if provided
    if (sortQuery && sortQuery.field && sortQuery.direction) {
      queries.push(orderBy(sortQuery.field, sortQuery.direction))
    }

    // Apply pagination
    queries.push(startAfter(lastVisible.value), limit(pageSize.value))

    const nextQuery = query(collRef, ...queries)

    try {
      const docSnapshots = await getDocs(nextQuery)
      const newDocuments = docSnapshots.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      documents.value.push(...newDocuments)
      lastVisible.value = docSnapshots.docs[docSnapshots.docs.length - 1]

      if (newDocuments.length < pageSize.value) {
        allDataLoaded.value = true
      }
    } catch (error) {
      console.error('Error fetching next page: ', error)
    }
  }

  const getDocumentSnapshot = async (collection, docId) => {
    loading.value = true
    documents.value = []
    const docRef = doc(firebase.db, collection, docId)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      documents.value = [{
        id: docSnap.id,
        ...docSnap.data()
      }]
      allDataLoaded.value = true
    }
    loading.value = false
  }

  const deleteDocumentsByDocumentIds = async (collectionId, documentIds) => {
    const batch = writeBatch(firebase.db)
    const deleteFirebaseUser = httpsCallable(firebase.functions, 'deleteFirebaseUser')

    for (const docId of documentIds) {
      const docRef = doc(firebase.db, collectionId, docId)
      batch.delete(docRef)

      if (['users', 'doctors', 'admin'].includes(collectionId)) {
        try {
          await deleteFirebaseUser({ uid: docId })
        } catch (err) {
          console.error(`Error deleting Firebase user: ${docId}`, err)
        }
      }
    }

    try {
      await batch.commit()
      console.log('Documents successfully deleted')
    } catch (error) {
      console.error('Error deleting documents: ', error)
    } finally {
      fetchDocuments(collectionId)
    }
  }

  return { documents, pageSize, allDataLoaded, loading, fetchDocuments, fetchNextPage, getDocumentSnapshot, deleteDocumentsByDocumentIds }
}

export default getCollection
